// API Security · Proposals — policy proposal evidence side drawer (v3 shell).

import React from "react";
import PropTypes from "prop-types";
import { Stack, Box, Typography, Chip, Button } from "@mui/material";
import {
  UserX, ShieldX, MapPin, Bot, Gauge, FileSearch,
} from "lucide-react";
import copy from "copy-to-clipboard";
import colors from "../../../../../../utils/colors";
import { DETECTION_ICONS } from "../../../../../../icons/detectionLucideIcons";
import WafEventDrawerShell from "../../../detection/WafEventDrawerShell";
import FindingDrawerHeader from "../../../detection/v3/FindingDrawerHeader";
import SmartParseRawJsonViewer from "../../../detection/v3/SmartParseRawJsonViewer";
import {
  SmartParseSectionCard as SectionCard,
  DrawerFieldStack,
} from "../../../detection/v3/smartParseDrawerUi";
import { TEXT_PRIMARY, secondaryButtonStyle } from "../../../detection/wafEventDrawerShared";
import ProposalActionButtons from "./ProposalActionButtons";

function categoriseProposal(p) {
  const tt = (p.target_table || "").toLowerCase();
  const reason = (p.reason || "").toLowerCase();
  if (tt.includes("authz")) return "authz";
  if (tt.includes("authn") && (reason.includes("geo") || reason.includes("travel") || reason.includes("step-up"))) return "geo";
  if (tt.includes("authn")) return "geo";
  if (reason.includes("ato") || reason.includes("takeover") || reason.includes("credential")) return "ato";
  if (reason.includes("scrap") || reason.includes("bot") || reason.includes("crawler")) return "bot";
  if (tt.includes("rate") || reason.includes("burst") || reason.includes("rate")) return "rate";
  return "rate";
}

function categoryIcon(cat) {
  if (cat === "ato") return UserX;
  if (cat === "authz") return ShieldX;
  if (cat === "geo") return MapPin;
  if (cat === "bot") return Bot;
  return Gauge;
}

function shortTitle(p) {
  const r = (p.reason || "").trim();
  if (!r) return "Policy proposal";
  const period = r.indexOf(".");
  const dash = r.indexOf(" — ");
  const cut = (period > 0 && period < 80) ? period
            : (dash > 0 && dash < 80) ? dash
            : Math.min(r.length, 80);
  return r.slice(0, cut);
}

function confidenceFromHits(n) {
  if (n == null) return null;
  if (n >= 10) return "high";
  if (n >= 3) return "medium";
  return "low";
}

const PILL_KINDS = {
  default: { bg: colors.neutral[100], fg: colors.neutral[700], bd: colors.neutral[200] },
  ok: { bg: "#E8FBEF", fg: "#0f7a42", bd: "#C0EED4" },
  danger: { bg: "#FEECEE", fg: "#A11228", bd: "#FBC9CF" },
  info: { bg: colors.primary[100], fg: colors.primary[500], bd: colors.primary[200] },
  indigo: { bg: "rgba(99,113,247,0.10)", fg: colors.primary[500], bd: "rgba(99,113,247,0.24)" },
};

function proposalStateMeta(state) {
  if (state === "draft") return { kind: "indigo", label: "Pending" };
  if (state === "reviewed" || state === "deferred") return { kind: "info", label: "Deferred" };
  if (state === "accepted") return { kind: "ok", label: state };
  if (state === "rejected") return { kind: "danger", label: state };
  return { kind: "default", label: state || "—" };
}

function ProposalStateChip({ state }) {
  const meta = proposalStateMeta(state);
  const p = PILL_KINDS[meta.kind] || PILL_KINDS.default;
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        width: "fit-content",
        maxWidth: "100%",
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        borderRadius: "999px",
        backgroundColor: p.bg,
        color: p.fg,
        border: `1px solid ${p.bd}`,
        "& .MuiChip-label": { px: 1.125, py: 0, lineHeight: 1.3 },
      }}
    />
  );
}

function formatEvidenceValue(value) {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const EVIDENCE_FIELD_KEYS = [
  "kind", "finding_kind", "identity", "subject", "route", "path", "uri",
  "method", "ip", "request_id", "hits", "count", "ts", "first_ts", "last_ts",
];

function evidenceItemFields(item, index) {
  if (item == null) return [];
  if (typeof item !== "object" || Array.isArray(item)) {
    return [{
      key: `item-${index}`,
      label: `Finding ${index + 1}`,
      value: formatEvidenceValue(item),
      copyable: true,
      monospace: true,
      fullRow: true,
    }];
  }

  const fields = [];
  for (const key of EVIDENCE_FIELD_KEYS) {
    if (item[key] != null && item[key] !== "") {
      fields.push({
        key: `${index}-${key}`,
        label: key.replace(/_/g, " "),
        value: formatEvidenceValue(item[key]),
        copyable: typeof item[key] === "string" || typeof item[key] === "number",
        monospace: ["identity", "subject", "route", "path", "uri", "request_id", "ip"].includes(key),
        fullRow: ["route", "path", "uri", "request_id"].includes(key),
      });
    }
  }

  const known = new Set(EVIDENCE_FIELD_KEYS);
  const extra = Object.keys(item).filter((k) => !known.has(k));
  for (const key of extra) {
    if (item[key] == null || item[key] === "") continue;
    fields.push({
      key: `${index}-extra-${key}`,
      label: key.replace(/_/g, " "),
      value: formatEvidenceValue(item[key]),
      copyable: typeof item[key] === "string" || typeof item[key] === "number",
      monospace: true,
      fullRow: true,
    });
  }

  return fields;
}

function EvidenceList({ evidence, onCopy }) {
  const items = Array.isArray(evidence) ? evidence : [];
  if (items.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: colors.neutral[700], lineHeight: "20px" }}>
        No supporting evidence rows were attached to this proposal yet. The proposer daemon may
        populate this section on the next mining cycle.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {items.map((item, index) => {
        const fields = evidenceItemFields(item, index);
        if (fields.length === 0) return null;
        return (
          <Box
            key={`evidence-${index}`}
            sx={{
              border: `1px solid ${colors.neutral[200]}`,
              borderRadius: "10px",
              backgroundColor: "#fff",
              px: 2,
              py: 1.5,
            }}
          >
            <Typography sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: colors.neutral[600],
              mb: 1.25,
            }}
            >
              Finding {index + 1}
            </Typography>
            <DrawerFieldStack columns={2} onCopy={onCopy} fields={fields} />
          </Box>
        );
      })}
    </Stack>
  );
}

export default function ProposalEvidenceDrawer({
  open, onClose, proposal, onAct, canConfigure, busy,
}) {
  const [snackOpen, setSnackOpen] = React.useState(false);

  const handleCopy = (value) => {
    if (!value || value === "—") return;
    copy(String(value));
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  if (!proposal) {
    return (
      <WafEventDrawerShell open={false} onClose={onClose} title="Proposal evidence">
        {null}
      </WafEventDrawerShell>
    );
  }

  const cat = categoriseProposal(proposal);
  const HeaderIcon = categoryIcon(cat);
  const title = shortTitle(proposal);
  const targetTable = (proposal.target_table || "rule").replace("api_security.", "");
  const confidence = confidenceFromHits(proposal.recent_hit_count);
  const evidence = Array.isArray(proposal.evidence) ? proposal.evidence : [];

  return (
    <>
      <WafEventDrawerShell
        open={open}
        onClose={onClose}
        title="Proposal evidence"
        headerVariant="hero"
        footerActions={(
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
            <Button onClick={onClose} sx={secondaryButtonStyle}>
              Close
            </Button>
            <ProposalActionButtons
              proposal={proposal}
              onAct={onAct}
              canConfigure={canConfigure}
              busy={busy}
            />
          </Stack>
        )}
        headerContent={(
          <FindingDrawerHeader
            embedded
            title={title}
            detailLine={`Target: ${targetTable}${proposal.recent_hit_count != null ? ` · ${proposal.recent_hit_count} recent hit${proposal.recent_hit_count === 1 ? "" : "s"}` : ""}`}
            icon={HeaderIcon}
            idLabel="ID"
            idValue={proposal.id}
            onCopyId={handleCopy}
          />
        )}
      >
        <SectionCard icon={DETECTION_ICONS.detection} title="Proposal summary">
          <DrawerFieldStack
            columns={2}
            onCopy={handleCopy}
            fields={[
              {
                key: "state",
                label: "State",
                content: <ProposalStateChip state={proposal.state} />,
              },
              confidence
                ? { key: "confidence", label: "Confidence", value: confidence }
                : null,
              {
                key: "target",
                label: "Target table",
                value: targetTable,
                copyable: true,
              },
              {
                key: "hits",
                label: "Recent hits",
                value: proposal.recent_hit_count != null ? String(proposal.recent_hit_count) : "—",
              },
              proposal.pattern_fingerprint
                ? {
                  key: "fingerprint",
                  label: "Pattern fingerprint",
                  value: proposal.pattern_fingerprint,
                  copyable: true,
                  monospace: true,
                  fullRow: true,
                }
                : null,
              proposal.created_at
                ? {
                  key: "created",
                  label: "Created",
                  value: new Date(proposal.created_at).toLocaleString(),
                }
                : null,
              proposal.reason
                ? {
                  key: "reason",
                  label: "Full reason",
                  value: proposal.reason,
                  copyable: true,
                  fullRow: true,
                }
                : null,
            ]}
          />
        </SectionCard>

        <SectionCard icon={FileSearch} title={`Supporting evidence (${evidence.length})`}>
          <EvidenceList evidence={evidence} onCopy={handleCopy} />
        </SectionCard>

        <SectionCard icon={DETECTION_ICONS.info} title="Raw data" defaultExpanded={false}>
          <SmartParseRawJsonViewer
            data={proposal}
            onCopy={handleCopy}
            filename="proposal_evidence.json"
          />
        </SectionCard>
      </WafEventDrawerShell>

      {snackOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            backgroundColor: TEXT_PRIMARY,
            color: "#fff",
            px: 2,
            py: 1,
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1400,
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#fff" }}>
            Copied to clipboard!
          </Typography>
        </Box>
      )}
    </>
  );
}

ProposalEvidenceDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  proposal: PropTypes.object,
  onAct: PropTypes.func,
  canConfigure: PropTypes.bool,
  busy: PropTypes.string,
};
